import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Line, Area, AreaChart
} from 'recharts';
import { FaChartPie, FaChartBar, FaSync, FaExpand, FaTimes, FaTrophy, FaRocket, FaUsers, FaLightbulb } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// رنگ‌های حرفه‌ای و پر کنتراست مناسب پرزنتیشن
const COLORS = [
  '#6366F1', '#EC4899', '#F59E0B', '#10B981', '#8B5CF6',
  '#EF4444', '#06B6D4', '#F97316', '#84CC16', '#A855F7',
  '#14B8A6', '#FB923C', '#22D3EE', '#34D399', '#60A5FA'
];

const PREMIUM_GRADIENTS = [
  ['#667eea', '#764ba2'],
  ['#f093fb', '#f5576c'],
  ['#4facfe', '#00f2fe'],
  ['#43e97b', '#38f9d7'],
  ['#fa709a', '#fee140'],
  ['#ff9a9e', '#fecfef'],
  ['#ffecd2', '#fcb69f'],
  ['#a18cd1', '#fbc2eb'],
];

// Tooltip حرفه‌ای با طراحی جذاب
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-gray-900 to-gray-800 p-5 rounded-2xl shadow-2xl border-2 border-primary-500"
      >
        <p className="font-black text-white text-lg mb-2">{label || payload[0].name}</p>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-primary-500 animate-pulse" />
          <p className="text-primary-300 text-2xl font-black">
            {payload[0].value} <span className="text-lg">طرح</span>
          </p>
        </div>
      </motion.div>
    );
  }
  return null;
};

// Label حرفه‌ای برای Pie Charts
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 0.03) return null;

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor="middle" 
      dominantBaseline="central" 
      className="text-base font-black"
      style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// کارت آماری مناسب پرزنتیشن
const PresentationStatCard = ({ title, value, icon: IconComponent, gradient, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 50, scale: 0.8 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ 
      delay: index * 0.15,
      type: "spring",
      stiffness: 80,
      damping: 12
    }}
    whileHover={{ 
      scale: 1.08, 
      y: -10,
      boxShadow: "0 30px 60px rgba(0,0,0,0.3)",
      transition: { duration: 0.3 }
    }}
    className="relative overflow-hidden rounded-[2rem] shadow-2xl cursor-pointer group"
    style={{ 
      background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`,
      minHeight: '180px'
    }}
  >
    {/* Background animated pattern */}
    <div className="absolute inset-0 opacity-20">
      <motion.div 
        animate={{ 
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360]
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute -right-10 -top-10 w-48 h-48 bg-white rounded-full blur-3xl"
      />
      <motion.div 
        animate={{ 
          scale: [1, 1.3, 1],
          rotate: [360, 180, 0]
        }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        className="absolute -left-10 -bottom-10 w-40 h-40 bg-white rounded-full blur-3xl"
      />
    </div>
    
    <div className="relative p-8 h-full flex flex-col justify-between">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <p className="text-white/95 text-base font-bold mb-3 tracking-wide uppercase">
            {title}
          </p>
          <motion.p 
            className="text-7xl font-black text-white"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              delay: index * 0.15 + 0.4, 
              type: "spring",
              stiffness: 100
            }}
          >
            {value}
          </motion.p>
        </div>
        <motion.div 
          className="text-white/30 group-hover:text-white/40"
          animate={{ 
            rotate: [0, 15, -15, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 3, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
        >
          <IconComponent size={70} />
        </motion.div>
      </div>
    </div>
    
    {/* Decorative corner elements */}
    <div className="absolute bottom-0 right-0 w-32 h-32 bg-white/10 rounded-tl-full group-hover:scale-150 transition-transform duration-700" />
    <div className="absolute top-0 left-0 w-24 h-24 bg-white/10 rounded-br-full group-hover:scale-150 transition-transform duration-700" />
    
    {/* Shine animation */}
    <motion.div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
      initial={{ x: "-100%", skewX: -20 }}
      animate={{ x: "200%" }}
      transition={{ 
        duration: 3, 
        repeat: Infinity, 
        repeatDelay: 2,
        ease: "easeInOut" 
      }}
    />
  </motion.div>
);

// کارت نمودار حرفه‌ای
const PresentationChartCard = ({ title, children, index, onExpand }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ 
      delay: index * 0.12,
      type: "spring",
      stiffness: 80
    }}
    whileHover={{ 
      y: -8,
      boxShadow: "0 30px 60px -12px rgba(0, 0, 0, 0.35)"
    }}
    className="bg-white dark:bg-gray-900 rounded-[2rem] shadow-2xl overflow-hidden border-4 border-gray-100 dark:border-gray-800 group relative"
  >
    {/* Gradient top border */}
    <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-primary-500 via-purple-500 to-pink-500" />
    
    <div className="p-6 border-b-4 border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-850">
      <div className="flex items-center gap-4">
        <div className="w-2 h-12 bg-gradient-to-b from-primary-500 via-purple-500 to-pink-500 rounded-full" />
        <h3 className="font-black text-gray-900 dark:text-white text-xl tracking-tight">
          {title}
        </h3>
      </div>
      <motion.button
        whileHover={{ scale: 1.15, rotate: 10 }}
        whileTap={{ scale: 0.9 }}
        onClick={onExpand}
        className="p-4 bg-gradient-to-br from-primary-600 via-purple-600 to-pink-600 text-white rounded-2xl hover:from-primary-700 hover:via-purple-700 hover:to-pink-700 transition-all shadow-xl hover:shadow-2xl"
        data-testid={`expand-${title}`}
      >
        <FaExpand className="text-lg" />
      </motion.button>
    </div>
    
    <div className="p-8 bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-900 dark:via-gray-850 dark:to-gray-900">
      {children}
    </div>
  </motion.div>
);

// Legend formatter
const LegendText = ({ value, entry }) => (
  <span className="text-gray-800 dark:text-gray-200 text-base font-bold">{entry.payload.name}</span>
);

function DetailedReports({ token }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChart, setActiveChart] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchStats = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/detailed-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError('خطا در دریافت آمار');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => fetchStats(), 30000);
    }
    return () => clearInterval(interval);
  }, [fetchStats, autoRefresh]);

  const legendFormatter = useMemo(() => {
    return (value, entry) => <LegendText value={value} entry={entry} />;
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] gap-6">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="w-20 h-20 border-8 border-primary-200 border-t-primary-600 rounded-full"
        />
        <motion.p
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="text-2xl font-bold text-gray-600 dark:text-gray-400"
        >
          در حال بارگذاری آمار...
        </motion.p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-12">
        <p className="text-red-500 text-xl mb-6 font-bold">{error}</p>
        <button
          onClick={() => fetchStats()}
          className="px-8 py-4 bg-primary-600 text-white rounded-2xl hover:bg-primary-700 transition-colors text-lg font-bold shadow-xl"
        >
          تلاش مجدد
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header - هدر حرفه‌ای مناسب پرزنتیشن */}
      <motion.div 
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 80 }}
        className="relative overflow-hidden rounded-[2rem] shadow-2xl border-4 border-primary-200 dark:border-gray-700"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        {/* Animated background */}
        <div className="absolute inset-0 opacity-20">
          <motion.div
            animate={{ 
              scale: [1, 1.5, 1],
              rotate: [0, 360]
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute -right-20 -top-20 w-64 h-64 bg-white rounded-full blur-3xl"
          />
          <motion.div
            animate={{ 
              scale: [1, 1.3, 1],
              rotate: [360, 0]
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            className="absolute -left-20 -bottom-20 w-56 h-56 bg-white rounded-full blur-3xl"
          />
        </div>

        <div className="relative p-8 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <motion.div 
              animate={{ 
                rotate: [0, 360],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 20, 
                repeat: Infinity, 
                ease: "linear" 
              }}
              className="p-5 bg-white/20 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-white/30"
            >
              <FaChartPie className="text-white text-5xl" />
            </motion.div>
            <div>
              <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tight drop-shadow-lg">
                گزارش تفکیکی آماری
              </h2>
              <div className="flex items-center gap-3 mt-3">
                <motion.div 
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-3 h-3 bg-green-400 rounded-full shadow-lg"
                />
                <p className="text-white/95 text-lg font-bold">
                  آخرین به‌روزرسانی: {stats?.last_updated ? new Date(stats.last_updated).toLocaleString('fa-IR') : '-'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-3 px-6 py-3 bg-white/20 backdrop-blur-lg rounded-2xl border-2 border-white/30 cursor-pointer hover:bg-white/30 transition-all shadow-xl">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded text-primary-600 focus:ring-primary-500 w-6 h-6 cursor-pointer"
              />
              <span className="text-base font-bold text-white">به‌روزرسانی خودکار</span>
            </label>
            
            <motion.button
              whileHover={{ scale: 1.1, boxShadow: "0 15px 35px rgba(255,255,255,0.3)" }}
              whileTap={{ scale: 0.9 }}
              onClick={() => fetchStats(true)}
              disabled={refreshing}
              className="flex items-center gap-3 px-8 py-4 bg-white text-primary-700 rounded-2xl font-black text-lg shadow-2xl hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaSync className={`text-xl ${refreshing ? 'animate-spin' : ''}`} />
              به‌روزرسانی
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Summary Cards - کارت‌های آماری حرفه‌ای */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <PresentationStatCard
          title="کل طرح‌ها"
          value={stats?.total_registrations || 0}
          icon={FaTrophy}
          gradient={PREMIUM_GRADIENTS[0]}
          index={0}
        />
        <PresentationStatCard
          title="حوزه‌های دانش‌بنیان"
          value={stats?.category_main_stats?.length || 0}
          icon={FaRocket}
          gradient={PREMIUM_GRADIENTS[1]}
          index={1}
        />
        <PresentationStatCard
          title="دسته‌های فناوری"
          value={stats?.category_tech_stats?.length || 0}
          icon={FaLightbulb}
          gradient={PREMIUM_GRADIENTS[2]}
          index={2}
        />
        <PresentationStatCard
          title="سطوح TRL"
          value={stats?.trl_stats?.length || 0}
          icon={FaUsers}
          gradient={PREMIUM_GRADIENTS[3]}
          index={3}
        />
      </div>

      {/* Charts Grid - نمودارهای اصلی */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* وضعیت */}
        <PresentationChartCard title="توزیع بر اساس وضعیت" index={0} onExpand={() => setActiveChart('وضعیت')}>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={stats?.status_stats || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={120}
                innerRadius={60}
                dataKey="value"
                animationBegin={0}
                animationDuration={1500}
              >
                {(stats?.status_stats || []).map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]}
                    stroke="#fff"
                    strokeWidth={3}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                formatter={legendFormatter}
                iconType="circle"
                iconSize={14}
                wrapperStyle={{ paddingTop: '20px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </PresentationChartCard>

        {/* مدرک تحصیلی */}
        <PresentationChartCard title="توزیع بر اساس مدرک تحصیلی" index={1} onExpand={() => setActiveChart('مدرک تحصیلی')}>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={stats?.education_stats || []} layout="vertical">
              <defs>
                {COLORS.map((color, index) => (
                  <linearGradient key={`gradient-${index}`} id={`barGradient${index}`} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                    <stop offset="100%" stopColor={color} stopOpacity={1} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fill: '#374151', fontSize: 13, fontWeight: 'bold' }} />
              <YAxis 
                dataKey="name" 
                type="category" 
                tick={{ fill: '#374151', fontSize: 13, fontWeight: 'bold' }} 
                width={120} 
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[0, 12, 12, 0]} animationDuration={1500}>
                {(stats?.education_stats || []).map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={`url(#barGradient${index % COLORS.length})`}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </PresentationChartCard>

        {/* سطح TRL */}
        <PresentationChartCard title="توزیع بر اساس سطح TRL" index={2} onExpand={() => setActiveChart('TRL')}>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={stats?.trl_stats || []}>
              <defs>
                <linearGradient id="trlGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity={1} />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.8} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fill: '#374151', fontSize: 13, fontWeight: 'bold' }} />
              <YAxis tick={{ fill: '#374151', fontWeight: 'bold' }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="value" 
                fill="url(#trlGradient)" 
                radius={[12, 12, 0, 0]} 
                animationDuration={1500}
              />
            </BarChart>
          </ResponsiveContainer>
        </PresentationChartCard>

        {/* ماهیت طرح */}
        <PresentationChartCard title="توزیع بر اساس ماهیت طرح" index={3} onExpand={() => setActiveChart('ماهیت طرح')}>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={stats?.project_nature_stats || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={120}
                innerRadius={60}
                dataKey="value"
                animationBegin={0}
                animationDuration={1500}
              >
                {(stats?.project_nature_stats || []).map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]}
                    stroke="#fff"
                    strokeWidth={3}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                formatter={legendFormatter}
                iconType="circle"
                iconSize={14}
                wrapperStyle={{ paddingTop: '20px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </PresentationChartCard>
      </div>

      {/* Full Width Charts - نمودارهای عرض کامل */}
      <div className="space-y-8">
        {/* حوزه دانش‌بنیان */}
        <PresentationChartCard title="توزیع بر اساس حوزه دانش‌بنیان" index={4} onExpand={() => setActiveChart('حوزه دانش‌بنیان')}>
          <ResponsiveContainer width="100%" height={450}>
            <BarChart data={stats?.category_main_stats || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fill: '#374151', fontSize: 13, fontWeight: 'bold' }} />
              <YAxis 
                dataKey="name" 
                type="category" 
                tick={{ fill: '#374151', fontSize: 12, fontWeight: 'bold' }} 
                width={220} 
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[0, 15, 15, 0]} animationDuration={1500}>
                {(stats?.category_main_stats || []).map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </PresentationChartCard>

        {/* دسته فناوری */}
        {stats?.category_tech_stats?.length > 0 && (
          <PresentationChartCard title="توزیع بر اساس دسته فناوری" index={5} onExpand={() => setActiveChart('دسته فناوری')}>
            <ResponsiveContainer width="100%" height={Math.max(400, stats?.category_tech_stats?.length * 45)}>
              <BarChart data={stats?.category_tech_stats || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fill: '#374151', fontSize: 13, fontWeight: 'bold' }} />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  tick={{ fill: '#374151', fontSize: 11, fontWeight: 'bold' }} 
                  width={280} 
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 15, 15, 0]} animationDuration={1500}>
                  {(stats?.category_tech_stats || []).map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </PresentationChartCard>
        )}

        {/* روند ثبت‌نام روزانه */}
        {stats?.daily_stats?.length > 0 && (
          <PresentationChartCard title="روند ثبت‌نام در ۷ روز اخیر" index={6} onExpand={() => setActiveChart('روند روزانه')}>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={stats?.daily_stats || []}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366F1" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#374151', fontSize: 13, fontWeight: 'bold' }} 
                />
                <YAxis tick={{ fill: '#374151', fontWeight: 'bold' }} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#6366F1"
                  strokeWidth={4}
                  fill="url(#colorCount)"
                  animationDuration={1500}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#6366F1"
                  strokeWidth={4}
                  dot={{ fill: '#6366F1', r: 8, strokeWidth: 3, stroke: '#fff' }}
                  activeDot={{ r: 10 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </PresentationChartCard>
        )}
      </div>

      {/* Statistics Table - جدول آماری */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, type: "spring" }}
        whileHover={{ boxShadow: "0 30px 60px -12px rgba(0, 0, 0, 0.35)" }}
        className="bg-white dark:bg-gray-900 rounded-[2rem] shadow-2xl overflow-hidden border-4 border-gray-100 dark:border-gray-800"
      >
        {/* Gradient top border */}
        <div className="h-2 bg-gradient-to-r from-primary-500 via-purple-500 to-pink-500" />
        
        <div className="p-8 border-b-4 border-gray-100 dark:border-gray-800 bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-850">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-gradient-to-br from-primary-600 via-purple-600 to-pink-600 rounded-2xl shadow-xl">
              <FaChartBar className="text-white text-3xl" />
            </div>
            <div>
              <h3 className="font-black text-gray-900 dark:text-white text-3xl">جدول خلاصه آماری</h3>
              <p className="text-lg text-gray-600 dark:text-gray-400 mt-2 font-bold">تحلیل کامل وضعیت طرح‌ها</p>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900">
              <tr>
                <th className="px-8 py-5 text-right text-base font-black text-gray-900 dark:text-white uppercase tracking-wider">
                  دسته‌بندی
                </th>
                <th className="px-8 py-5 text-center text-base font-black text-gray-900 dark:text-white uppercase tracking-wider">
                  تعداد
                </th>
                <th className="px-8 py-5 text-center text-base font-black text-gray-900 dark:text-white uppercase tracking-wider">
                  درصد
                </th>
              </tr>
            </thead>
            <tbody className="divide-y-2 divide-gray-200 dark:divide-gray-800">
              {stats?.status_stats?.map((item, index) => (
                <motion.tr 
                  key={`status-${index}`}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 + index * 0.08 }}
                  whileHover={{ 
                    backgroundColor: 'rgba(99, 102, 241, 0.08)',
                    scale: 1.01
                  }}
                  className="transition-all duration-200"
                >
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <motion.div 
                        whileHover={{ scale: 1.4, rotate: 180 }}
                        className="w-5 h-5 rounded-full shadow-xl border-2 border-white" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }} 
                      />
                      <span className="font-black text-gray-900 dark:text-white text-lg">{item.name}</span>
                    </div>
                  </td>
                  <td className="px-8 py-5 text-center">
                    <span className="inline-block px-6 py-3 bg-gradient-to-r from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30 text-primary-700 dark:text-primary-300 font-black rounded-2xl text-xl shadow-lg">
                      {item.value}
                    </span>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center justify-center gap-3">
                      <div className="relative w-32 h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden shadow-inner">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ 
                            width: `${stats.total_registrations > 0 ? (item.value / stats.total_registrations) * 100 : 0}%` 
                          }}
                          transition={{ delay: 1 + index * 0.08, duration: 1, ease: "easeOut" }}
                          className="h-full rounded-full shadow-lg"
                          style={{ 
                            background: `linear-gradient(90deg, ${COLORS[index % COLORS.length]}, ${COLORS[(index + 1) % COLORS.length]})`
                          }}
                        />
                      </div>
                      <span className="font-black text-gray-900 dark:text-white text-xl min-w-[4rem]">
                        {stats.total_registrations > 0 ? ((item.value / stats.total_registrations) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Fullscreen Modal - مودال تمام صفحه */}
      <AnimatePresence>
        {activeChart && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveChart(null)}
              className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50"
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.7, y: 100 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.7, y: 100 }}
                transition={{ type: "spring", damping: 20, stiffness: 100 }}
                className="bg-white dark:bg-gray-900 rounded-[2rem] shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-auto border-4 border-primary-200 dark:border-gray-700"
              >
                {/* Header */}
                <div className="sticky top-0 z-10 p-8 border-b-4 border-primary-200 dark:border-gray-800 bg-gradient-to-r from-primary-600 via-purple-600 to-pink-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-5">
                      <div className="w-3 h-16 bg-white rounded-full" />
                      <div>
                        <h3 className="text-4xl font-black text-white drop-shadow-lg">{activeChart}</h3>
                        <p className="text-lg text-white/90 mt-2 font-bold">نمایش تمام صفحه - مناسب پرزنتیشن</p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.15, rotate: 90 }}
                      whileTap={{ scale: 0.85 }}
                      onClick={() => setActiveChart(null)}
                      className="p-5 bg-white/20 backdrop-blur-lg hover:bg-white/30 rounded-2xl transition-all border-2 border-white/30"
                      data-testid="close-fullscreen"
                    >
                      <FaTimes className="text-white text-3xl" />
                    </motion.button>
                  </div>
                </div>
                
                {/* Chart Content */}
                <div className="p-12 bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-900 dark:via-gray-850 dark:to-gray-900">
                  {activeChart === 'وضعیت' && (
                    <ResponsiveContainer width="100%" height={650}>
                      <PieChart>
                        <Pie
                          data={stats?.status_stats || []}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={renderCustomizedLabel}
                          outerRadius={220}
                          innerRadius={110}
                          dataKey="value"
                          animationBegin={0}
                          animationDuration={1500}
                        >
                          {(stats?.status_stats || []).map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS[index % COLORS.length]}
                              stroke="#fff"
                              strokeWidth={4}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                          formatter={legendFormatter}
                          iconType="circle"
                          iconSize={18}
                          wrapperStyle={{ paddingTop: '30px', fontSize: '16px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                  
                  {activeChart === 'مدرک تحصیلی' && (
                    <ResponsiveContainer width="100%" height={650}>
                      <BarChart data={stats?.education_stats || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tick={{ fill: '#374151', fontSize: 16, fontWeight: 'bold' }} />
                        <YAxis 
                          dataKey="name" 
                          type="category" 
                          tick={{ fill: '#374151', fontSize: 16, fontWeight: 'bold' }} 
                          width={180} 
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" radius={[0, 15, 15, 0]} animationDuration={1500}>
                          {(stats?.education_stats || []).map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                  
                  {activeChart === 'TRL' && (
                    <ResponsiveContainer width="100%" height={650}>
                      <BarChart data={stats?.trl_stats || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fill: '#374151', fontSize: 16, fontWeight: 'bold' }} />
                        <YAxis tick={{ fill: '#374151', fontSize: 16, fontWeight: 'bold' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" radius={[15, 15, 0, 0]} animationDuration={1500}>
                          {(stats?.trl_stats || []).map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                  
                  {activeChart === 'ماهیت طرح' && (
                    <ResponsiveContainer width="100%" height={650}>
                      <PieChart>
                        <Pie
                          data={stats?.project_nature_stats || []}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={renderCustomizedLabel}
                          outerRadius={220}
                          innerRadius={110}
                          dataKey="value"
                          animationBegin={0}
                          animationDuration={1500}
                        >
                          {(stats?.project_nature_stats || []).map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS[index % COLORS.length]}
                              stroke="#fff"
                              strokeWidth={4}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                          formatter={legendFormatter}
                          iconType="circle"
                          iconSize={18}
                          wrapperStyle={{ paddingTop: '30px', fontSize: '16px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                  
                  {activeChart === 'حوزه دانش‌بنیان' && (
                    <ResponsiveContainer width="100%" height={Math.max(650, (stats?.category_main_stats?.length || 0) * 60)}>
                      <BarChart data={stats?.category_main_stats || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tick={{ fill: '#374151', fontSize: 16, fontWeight: 'bold' }} />
                        <YAxis 
                          dataKey="name" 
                          type="category" 
                          tick={{ fill: '#374151', fontSize: 14, fontWeight: 'bold' }} 
                          width={300} 
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" radius={[0, 15, 15, 0]} animationDuration={1500}>
                          {(stats?.category_main_stats || []).map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                  
                  {activeChart === 'دسته فناوری' && (
                    <ResponsiveContainer width="100%" height={Math.max(650, (stats?.category_tech_stats?.length || 0) * 55)}>
                      <BarChart data={stats?.category_tech_stats || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tick={{ fill: '#374151', fontSize: 16, fontWeight: 'bold' }} />
                        <YAxis 
                          dataKey="name" 
                          type="category" 
                          tick={{ fill: '#374151', fontSize: 13, fontWeight: 'bold' }} 
                          width={320} 
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" radius={[0, 15, 15, 0]} animationDuration={1500}>
                          {(stats?.category_tech_stats || []).map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                  
                  {activeChart === 'روند روزانه' && (
                    <ResponsiveContainer width="100%" height={650}>
                      <AreaChart data={stats?.daily_stats || []}>
                        <defs>
                          <linearGradient id="colorCountFull" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366F1" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#6366F1" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fill: '#374151', fontSize: 16, fontWeight: 'bold' }} 
                        />
                        <YAxis tick={{ fill: '#374151', fontSize: 16, fontWeight: 'bold' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="count"
                          stroke="#6366F1"
                          strokeWidth={5}
                          fill="url(#colorCountFull)"
                          animationDuration={1500}
                        />
                        <Line
                          type="monotone"
                          dataKey="count"
                          stroke="#6366F1"
                          strokeWidth={5}
                          dot={{ fill: '#6366F1', r: 10, strokeWidth: 4, stroke: '#fff' }}
                          activeDot={{ r: 14 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default DetailedReports;
